import { useMemo } from "react";

import { PERSPECTIVE } from "./forgeWikiLayouts";
import { useForgeStageCore } from "./useForgeStageCore";

/**
 * The cards as DOM elements, moved by CSS transforms.
 * ---------------------------------------------------------------------------
 * This was the whole stage - the camera, the tweens, the arrangements and the
 * pointer, as well as the painting. All of that is in useForgeStageCore now,
 * because the GPU surface next door needs exactly the same scene and writing
 * the camera twice is how the two end up disagreeing about where a card is.
 *
 * What is left here is the part that is genuinely about the DOM: one
 * `transform` string per card per frame, one for the world they sit in, and a
 * scale on the box outside the perspective. The camera above says what those
 * strings have to add up to; this says how CSS is told.
 *
 * Why the magnification is a scale on a separate element rather than the
 * camera moving in: the perspective is declared on .forge-wiki-stage__zoom and
 * must not change while the scene is being looked at, and a 2D scale outside
 * it magnifies the rendered result rather than the projection - which is also
 * what keeps the type sharp at twelve times life size. See ForgeWiki.css.
 * ---------------------------------------------------------------------------
 *
 * @param stageRef  the viewport, which clips
 * @param zoomRef   the box inside it carrying `perspective` and the zoom
 * @param worldRef  the element inside that one the cards are children of
 * @returns the same handles the core does - what the pages call
 */
export function useForgeWikiStage(stageRef, zoomRef, worldRef, options = {}) {
  /* One card's own controls: a press on any of these is theirs, not the
     stage's, so it never becomes a turn and never swallows the click. */
  const OWN = ".MuiInputBase-root, button, [role='combobox'], a";

  const surface = useMemo(() => ({
    count: () => worldRef.current?.children.length || 0,

    /* Which card the pointer is on, as the DOM has always answered it: the
       element it is over, and its place among its siblings - which is the same
       order the scene's objects are in. */
    cardAt: (e) => {
      const card = e.target.closest && e.target.closest(".forge-wiki-card");
      if (!card) return -1;
      if (e.target.closest(OWN)) return -2;
      return Array.prototype.indexOf.call(worldRef.current?.children || [], card);
    },

    /* The list and the reading panel are ordinary page furniture: they scroll,
       their links and controls are clicked, and their text is selected. Both
       sit inside the stage, so every one of those events passes the stage's
       handlers first - and they have to let them through, or the document can
       be read but never used and the list never scrolled. */
    guard: (e) => !!(e.target.closest
      && e.target.closest(".forge-wiki-stack-doc, .forge-stack-list")),

    paint: (cards, cam) => {
      const world = worldRef.current;
      if (!world) return;

      const zoomEl = zoomRef.current;
      if (zoomEl) zoomEl.style.transform = `scale(${cam.zoom.toFixed(4)})`;

      world.style.transform =
        `translateZ(${(PERSPECTIVE - cam.distance).toFixed(2)}px)`
        // where the point being turned about sits on screen
        + ` translate3d(${cam.ox.toFixed(2)}px, ${cam.oy.toFixed(2)}px, 0px)`
        + ` rotateX(${cam.pitch.toFixed(4)}rad)`
        + ` rotateY(${cam.yaw.toFixed(4)}rad)`
        // the point the camera turns about is brought to the origin, which is
        // where the lens is pointed
        + ` translate3d(${(-cam.tx).toFixed(2)}px, ${(-cam.ty).toFixed(2)}px, ${(-cam.tz).toFixed(2)}px)`;

      const els = world.children;
      for (let i = 0; i < els.length; i++) {
        const o = cards[i];
        if (!o) continue;
        /* fx/fy/fz, not rx/ry/rz: the facing the core worked out for this
           frame, which is the arrangement's own until follow mode turns the
           card towards the screen. */
        /* ...and how big: the chosen card is drawn larger and its neighbours
           smaller, which the core works out for both renderers. */
        const k = o.k === undefined ? 1 : o.k;
        els[i].style.transform =
          `translate3d(${o.x.toFixed(2)}px, ${o.y.toFixed(2)}px, ${o.z.toFixed(2)}px)`
          + ` rotateY(${o.fy.toFixed(4)}rad) rotateX(${o.fx.toFixed(4)}rad) rotateZ(${o.fz.toFixed(4)}rad)`
          + (k === 1 ? "" : ` scale(${k.toFixed(3)})`);
      }
    },
  }), [worldRef, zoomRef, OWN]);

  return useForgeStageCore(stageRef, { ...options, surface });
}

export default useForgeWikiStage;
